import { useState } from 'react';
import { API } from '../lib/api';
import { VideoCamera, CalendarCheck, CalendarPlus } from '@phosphor-icons/react';

const STATUS_TONE = { proposed: 'bg-amber-100 text-amber-800', confirmed: 'bg-green-100 text-green-800', declined: 'bg-rose-100 text-rose-700', cancelled: 'bg-slate-100 text-slate-500' };
const fmt = (s) => (s || '').replace('T', ' · ').slice(0, 18);

export const MeetingCard = ({ meeting: m, isStaff, userId, plugins, accept, decline, cancel }) => {
  const [slot, setSlot] = useState(m.slots?.[0] || '');
  const enabled = (plugins || []).filter((p) => p.enabled);
  const [provider, setProvider] = useState(enabled[0]?.provider || '');
  const isRequester = m.requested_by === userId;

  return (
    <div data-testid={`meeting-card-${m.id}`} className="mt-2 border border-orange-200 bg-orange-50/60 rounded-md p-3 text-slate-800 min-w-[240px]">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarCheck size={16} className="text-orange-600" weight="duotone" />
        <span className="font-semibold text-xs">Parent-teacher meeting</span>
        <span className={`font-mono-label text-[8px] uppercase px-1.5 py-0.5 rounded ${STATUS_TONE[m.status] || 'bg-slate-100'}`} data-testid={`meeting-status-${m.id}`}>{m.status}</span>
      </div>
      {m.purpose && <p className="text-xs mt-1">{m.purpose}</p>}
      <p className="text-[10px] text-slate-500 mt-0.5">requested by {m.requested_by_name}</p>

      {m.status === 'proposed' && (
        <div className="mt-2 space-y-1.5">
          {(m.slots || []).map((s) => (
            <label key={s} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="radio" name={`slot-${m.id}`} checked={slot === s} onChange={() => setSlot(s)} disabled={!isStaff} data-testid={`meeting-slot-${m.id}-${s}`} />
              {fmt(s)}
            </label>
          ))}
          {isStaff && (
            <div className="flex gap-1.5 flex-wrap items-center pt-1">
              <select data-testid={`meeting-provider-select-${m.id}`} value={provider} onChange={(e) => setProvider(e.target.value)}
                className="border border-orange-200 rounded px-1.5 py-1 text-xs bg-white">
                {enabled.length === 0 && <option value="">No plugin enabled</option>}
                {enabled.map((p) => <option key={p.provider} value={p.provider}>{p.label}</option>)}
              </select>
              <button data-testid={`meeting-accept-${m.id}`} disabled={!slot || !provider}
                onClick={() => accept({ id: m.id, slot, provider })}
                className="text-xs bg-orange-600 text-white rounded px-2 py-1 hover:bg-orange-500 disabled:opacity-50">Confirm</button>
              <button data-testid={`meeting-decline-${m.id}`} onClick={() => decline(m.id)}
                className="text-xs border border-rose-200 text-rose-600 rounded px-2 py-1 hover:bg-rose-50">Decline</button>
            </div>
          )}
          {isRequester && (
            <button data-testid={`meeting-cancel-${m.id}`} onClick={() => cancel(m.id)}
              className="text-xs text-slate-500 underline">Cancel request</button>
          )}
        </div>
      )}

      {m.status === 'confirmed' && (
        <div className="mt-2">
          <p className="text-xs font-semibold">{fmt(m.chosen_slot)} · {m.provider_label}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <a data-testid={`meeting-join-${m.id}`} href={m.join_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs bg-orange-600 text-white rounded px-2.5 py-1.5 hover:bg-orange-500">
              <VideoCamera size={14} weight="fill" /> Join {m.provider_label}
            </a>
            {m.link_kind === 'placeholder' && <span className="font-mono-label text-[8px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">demo link - add plugin creds to go live</span>}
            {m.link_kind === 'live' && <span className="font-mono-label text-[8px] uppercase px-1.5 py-0.5 rounded bg-green-100 text-green-700">live</span>}
            <a data-testid={`meeting-ics-${m.id}`} href={`${API}/meetings/${m.id}/ics`}
              className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded px-2 py-1.5 hover:border-orange-400 hover:text-orange-700 transition-colors">
              <CalendarPlus size={13} /> Add to calendar
            </a>
            {(isStaff || isRequester) && (
              <button data-testid={`meeting-cancel-${m.id}`} onClick={() => cancel(m.id)} className="text-xs text-slate-500 underline">Cancel</button>
            )}
          </div>
        </div>
      )}

      {m.status === 'declined' && m.decline_reason && <p className="text-xs text-rose-600 mt-1">Reason: {m.decline_reason}</p>}
    </div>
  );
};
